function getQrContext() {
  const prefix = window.QRSHOP_BASE?.prefix;

  if (!prefix) return {};

  try {
    const raw = window.sessionStorage?.getItem(`${prefix}ctx`);
    return raw ? JSON.parse(raw) || {} : {};
  } catch (e) {
    return {};
  }
}

function getPromotionPayload(el) {
  const dataset = el?.dataset || {};
  const payload = {
    promotion_id: dataset.promotionId || dataset.postId || '',
    promotion_name: dataset.promotionName || '',
    creative_name: dataset.creativeName || '',
    creative_slot: dataset.creativeSlot || '',
    campaign_name: dataset.campaignName || '',
    shop_id: dataset.shopId || '',
    shop_slug: dataset.shopSlug || '',
    page_type: dataset.pageType || '',
    banner_format: dataset.bannerFormat || '',
    banner_position: dataset.bannerPosition || '',
    click_action: dataset.clickAction || '',
  };
  const qrContext = getQrContext();

  if (qrContext.src) payload.qr_src = qrContext.src;
  if (qrContext.loc) payload.qr_loc = qrContext.loc;
  if (qrContext.sid) payload.qr_sid = qrContext.sid;

  if (payload.campaign_name === '') {
    delete payload.campaign_name;
  }

  payload.items = [
    {
      promotion_id: payload.promotion_id,
      promotion_name: payload.promotion_name,
      creative_name: payload.creative_name,
      creative_slot: payload.creative_slot,
    },
  ];

  return payload;
}

function isTrackingEnabled(el) {
  return el?.dataset?.gaTrackingEnabled !== '0';
}

function sendPromotionEvent(eventName, el, callback) {
  if (!isTrackingEnabled(el)) {
    callback?.();
    return;
  }

  const payload = getPromotionPayload(el);

  if (typeof window.gtag !== 'function') {
    callback?.();
    return;
  }

  let completed = false;
  const done = () => {
    if (completed) return;
    completed = true;
    callback?.();
  };

  window.gtag('event', eventName, {
    ...payload,
    event_callback: done,
    event_timeout: 500,
  });

  if (callback) {
    window.setTimeout(done, 550);
  }
}

function getResolvedClickAction(el) {
  const action = el.dataset.clickAction || 'auto';
  const url = el.dataset.bannerUrl || '';

  if (action === 'auto') {
    return url ? 'link' : 'sheet';
  }

  return action;
}

function openBanner(el) {
  const action = getResolvedClickAction(el);
  const postId = Number(el.dataset.postId || 0);
  const url = el.dataset.bannerUrl || '';
  const target = el.dataset.linkTarget || '_blank';

  if (action === 'none') return;

  if (action === 'sheet') {
    window.Alpine?.store('sheet')?.openById(postId, 'banner');
    return;
  }

  if (action === 'link' && url) {
    if (target === '_self') {
      window.location.href = url;
      return;
    }

    window.open(url, '_blank', 'noopener');
  }
}

function handleBannerClick(event) {
  const el = event.target.closest('[data-ga-banner]');

  if (!el) return;

  event.preventDefault();

  const action = getResolvedClickAction(el);
  const target = el.dataset.linkTarget || '_blank';
  const shouldDelayOpen = action === 'link' && target === '_self';

  if (shouldDelayOpen) {
    sendPromotionEvent('select_promotion', el, () => {
      openBanner(el);
    });
    return;
  }

  sendPromotionEvent('select_promotion', el);
  openBanner(el);
}

function handleBannerKeydown(event) {
  if (event.key !== 'Enter' && event.key !== ' ') return;

  const el = event.target.closest('[data-ga-banner]');

  if (!el) return;

  event.preventDefault();
  el.click();
}

function getVisibleBanner(root) {
  const swiper = root?.swiper;

  if (swiper?.slides?.length) {
    return swiper.slides[swiper.activeIndex]?.querySelector('[data-ga-banner]');
  }

  return root?.querySelector('[data-ga-banner]');
}

function createImpressionKey(el) {
  return [
    el.dataset.promotionId || el.dataset.postId || '',
    el.dataset.creativeSlot || '',
    el.dataset.bannerPosition || '',
  ].join(':');
}

function trackAllImpressions(seen) {
  document.querySelectorAll('[data-ga-banner]').forEach((el) => {
    if (!isTrackingEnabled(el)) return;

    const key = createImpressionKey(el);
    if (seen.has(key)) return;

    seen.add(key);
    sendPromotionEvent('view_promotion', el);
  });
}

function trackStaticBanner(el, seen) {
  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      if (!isTrackingEnabled(el)) return;

      const key = createImpressionKey(el);
      if (seen.has(key)) return;

      seen.add(key);
      sendPromotionEvent('view_promotion', el);
      observer.disconnect();
    },
    {
      threshold: 0.5,
    }
  );

  observer.observe(el);
}

function initSwiperBannerImpressions(seen) {
  document.querySelectorAll('[data-swiper-banner]').forEach((root) => {
    let inView = false;

    const trackActiveBanner = () => {
      if (!inView) return;

      const el = getVisibleBanner(root);
      if (!el || !isTrackingEnabled(el)) return;

      const key = createImpressionKey(el);
      if (seen.has(key)) return;

      seen.add(key);
      sendPromotionEvent('view_promotion', el);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        inView = entries.some((entry) => entry.isIntersecting);
        trackActiveBanner();
      },
      {
        threshold: 0.5,
      }
    );

    observer.observe(root);

    if (root.swiper) {
      root.swiper.on('slideChangeTransitionEnd', trackActiveBanner);
      root.swiper.on('init', trackActiveBanner);
    }

    trackActiveBanner();
  });
}

function initStaticBannerImpressions(seen) {
  document.querySelectorAll('[data-ga-banner]').forEach((el) => {
    if (el.closest('[data-swiper-banner]')) return;

    trackStaticBanner(el, seen);
  });
}

function initBannerImpressions() {
  const seen = new Set();

  if (typeof window.IntersectionObserver !== 'function') {
    trackAllImpressions(seen);
    return;
  }

  initSwiperBannerImpressions(seen);
  initStaticBannerImpressions(seen);
}

export function initBannerTracking() {
  document.addEventListener('click', handleBannerClick);
  document.addEventListener('keydown', handleBannerKeydown);
  initBannerImpressions();
}
